import EventTarget from '@ungap/event-target';
import { nextTick } from '../nextTick.js';

export class ServiceWorkerInitializer extends EventTarget {
  #updateComplete = Promise.withResolvers();

  constructor(appHasDeviceAccess = true, serviceWorkerLocation = './sw.js') {
    super();
    this.appHasDeviceAccess = appHasDeviceAccess;
    this.serviceWorkerLocation = serviceWorkerLocation;
    this._updating = null;

    this.addEventListener(
      'new-serviceworker-ready',
      () => {
        this._newServiceWorkerReady = true;
      },
      { once: true },
    );

    this._setupFrameMessageChannel = this.setupFrameMessageChannel.bind(this);
    window.addEventListener('message', this._setupFrameMessageChannel);
  }

  /**
   * Retrieves the active service worker.
   *
   * @return {ServiceWorker} The active service worker.
   */
  get activeServiceWorker() {
    return this.registration?.active;
  }

  /**
   * Retrieves the waiting service worker.
   *
   * @return {ServiceWorker | null} The waiting service worker or null if none is found.
   */
  get waitingServiceWorker() {
    return this.registration?.waiting;
  }

  /**
   * Retrieve the incoming service worker.
   *
   * @return {ServiceWorker | null} The incoming service worker or null if not available.
   */
  get incomingServiceWorker() {
    return this.registration.installing;
  }

  get allAvailableServiceworkers() {
    return [this.incomingServiceWorker, this.waitingServiceWorker, this.activeServiceWorker].filter(
      worker => worker,
    );
  }

  /**
   * Returns whether the ServiceWorker was installed in a previous session but the update was not triggered.
   *
   * @return {boolean} Whether the ServiceWorker was installed in a previous session but the update was not triggered.
   */
  get priorInstalledServiceWorkerNeverUpdated() {
    return this.waitingServiceWorker && this.activeServiceWorker;
  }

  /**
   * Returns a promise that resolves when the new service worker is ready.
   *
   * @return {Promise<ServiceWorkerInitializer>} A promise that resolves with the active service worker.
   */
  get newServiceWorkerReady() {
    return new Promise(resolve => {
      if (this._newServiceWorkerReady) resolve(this);
      else
        this.addEventListener('new-serviceworker-ready', () => resolve(this), {
          once: true,
        });
    });
  }

  /**
   * Returns whether this is the first installation of the service worker.
   *
   * @return {boolean} Returns true if this is the first installation of the service worker, otherwise false.
   */
  get isFirstInstallOfServiceWorker() {
    return this.incomingServiceWorker && !this.activeServiceWorker;
  }

  /**
   * Checks if the first installation is complete.
   *
   * @return {boolean} Returns true if the first installation is complete, otherwise false.
   */
  get isFirstInstallComplete() {
    return this.waitingServiceWorker?.state === 'installed' && !this.activeServiceWorker;
  }

  /**
   * Returns true if the service worker has finished installing and is currently active.
   *
   * @return {boolean} - true if the service worker has finished installing and is currently active
   */
  get isUpdateInstallComplete() {
    return this.waitingServiceWorker?.state === 'installed' && this.activeServiceWorker;
  }

  /**
   * Returns a Promise that resolves to a boolean indicating whether an updated ServiceWorker has been found.
   *
   * @return {Promise<boolean>} A Promise that resolves to a boolean value. True if an update has been found, false otherwise.
   */
  get updateComplete() {
    return this.#updateComplete.promise;
  }

  /**
   * Sets up the frame message channel to get messages from a host window.
   *
   * @param {Event} event - The message event object containing the setup data.
   */
  async setupFrameMessageChannel(event) {
    if (event.data.type !== 'setup_message_channel') return this;
    const { setupFrameMessageChannel } = await import('./blockHostInitialization.js');
    await setupFrameMessageChannel.call(this, event);
    return this;
  }

  /**
   * Raise an event for messages coming through to detect milestone events
   * @param {MessageEvent} event the Event from a 'message' event
   * @param {ServiceWorkerMessage} event.data the data passed through the 'message'
   */
  raiseEventsForIncomingMessages() {
    navigator.serviceWorker.addEventListener('message', ({ data: { type } }) => {
      this.dispatchEvent(new Event(type));
    });
  }

  /**
   * an update was found for the ServiceWorker, once it is done installing, notify the user of the update's availability
   */
  finishInstallAndNotify() {
    this._updating = true;
    this.raiseEventsForIncomingMessages();
    this.incomingServiceWorker.addEventListener('statechange', () => {
      if (this.isUpdateInstallComplete) {
        this.notifyUpdateAvailable();
      }
    });
  }

  onInstallTakeControl() {
    if (!this.isFirstInstallComplete) return;
    this._firstInstall = true;
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => {
        this.dispatchEvent(new Event('first-install-complete'));
        this.dispatchEvent(new Event('new-serviceworker-ready'));
      },
      { once: true },
    );
    this.waitingServiceWorker.postMessage({ type: 'FIRST_INSTALL' });
  }

  async groupCollectionCompleted() {
    this.activeServiceWorker.postMessage({ type: 'GROUP_COLLECTION_COMPLETE' });
  }

  /**
   * checks the app is in an embed state and is the host then events the host is ready and triggers the initialized attribute
   * @return {Promise<ServiceWorkerInitializer>}
   */
  async notifyHostInitialized(services) {
    if (!this.hostMessageChannel) return this;
    const { blockHostInitialization } = await import('./blockHostInitialization.js');
    await blockHostInitialization.call(this, services);
    return this;
  }

  /**
   * Initializes the service worker.
   *
   * @return {ServiceWorkerInitializer} - The initializer.
   */
  async init() {
    if (this.registration) return this;
    if (!('serviceWorker' in navigator)) {
      const { handleCordova } = await import('./handleCordova.js');
      return handleCordova();
    }
    this.registration = await navigator.serviceWorker.register(this.serviceWorkerLocation, {
      scope: './',
    });
    this.allAvailableServiceworkers.forEach(worker =>
      worker.postMessage({
        type: window.location.search.includes('demo-mode') ? 'DEMO-MODE' : 'NOT-DEMO-MODE',
      }),
    );
    if (this.registration) {
      if (!this.isFirstInstallOfServiceWorker) {
        this.registration.addEventListener('updatefound', () => this.finishInstallAndNotify()); // this will give a false positive on first install
        try {
          await this.registration.update();
          await nextTick();
        } catch (error) {
          if (navigator.onLine) console.error(error);
          else
            console.warn('Cannot perform a serviceworker update while offline. Skipping update.');
        }
      }
      if (this._updating === null) {
        this.dispatchEvent(new Event('update-not-found'));
        this.#updateComplete.resolve(false);
      }
      this._updating = false;

      if (this.priorInstalledServiceWorkerNeverUpdated) this.notifyUpdateAvailable();

      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (this._firstInstall || PLATFORM_ID !== 'web') return;
        window.location.reload();
      });
    }

    if (this.isFirstInstallOfServiceWorker) {
      this.incomingServiceWorker.addEventListener('statechange', () => this.onInstallTakeControl());
    } else {
      // wait for a service worker to be active
      await navigator.serviceWorker.ready;
      this.dispatchEvent(new Event('new-serviceworker-ready'));
    }
    return this;
  }

  /**
   * Notifies the window that an update is available.
   */
  notifyUpdateAvailable() {
    if (PLATFORM_ID !== 'web') {
      this.activeServiceWorker.postMessage({ type: 'SKIP_WAITING', claim: true });
    } else {
      window._updateAvailable = true;
      this.dispatchEvent(new CustomEvent('update-available'));
      window.dispatchEvent(new CustomEvent('update-available'));
      this.#updateComplete.resolve(true);
    }
    this._updating = false;
  }

  /**
   * Posts to the waiting serviceworker to skip waiting and claim the clients.
   */
  skipWaitingAndClaim() {
    this.waitingServiceWorker.postMessage({ type: 'SKIP_WAITING', claim: true });
  }
}

export const serviceWorkerInitializer = new ServiceWorkerInitializer();

/**
 * @typedef {import('./ServiceWorkerInitializer.types.d.ts').ServiceWorkerMessage} ServiceWorkerMessage
 * @typedef {import('./ServiceWorkerInitializer.types.d.ts').JoinedMessage} JoinedMessage
 */
